import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DispatchStop, TourRouteLeg } from './dispatch-planner.component';
import { getStopServiceTypeFullLabel, getStopServiceTypeLabel } from './dispatch-planner-marker-info';
import { formatDispatchTermin } from './dispatch-planner-stop-format';

export interface DispatchTourMeta {
  distanceText?: string;
  durationText?: string;
  legs?: TourRouteLeg[];
  routePath?: { lat: number; lng: number }[];
  generatedAt?: Date;
  branch?: { lat: number; lng: number } | null;
  teamNumber?: number;
  teamLabel?: string;
  tourDate?: string;
  vehicle?: string;
}

interface RollCardTotals {
  volumen: number;
  gewicht: number;
  kollo: number;
  me: number;
  montageMin: number;
}

@Injectable({
  providedIn: 'root'
})
export class DispatchPlannerPdfService {
  async exportRollCard(stops: DispatchStop[], tour: DispatchTourMeta = {}): Promise<void> {
    const doc = new jsPDF('l', 'mm', 'a4');
    const margin = 8;
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 12;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('ROLLKARTE – MONTAGETEAM', margin, y);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const metaX = pageWidth - margin;
    const tourDate = tour.tourDate || this.resolveTourDate(stops, tour.generatedAt);
    const teamLabel = tour.teamLabel || (tour.teamNumber ? `Team ${tour.teamNumber}` : '–');
    const vehicle = (tour.vehicle || '').trim() || '–';

    doc.text(`Tour: ${tourDate}`, metaX, y - 2, { align: 'right' });
    doc.text(`Team: ${teamLabel}`, metaX, y + 3, { align: 'right' });
    doc.text(`Fahrzeug: ${vehicle}`, metaX, y + 8, { align: 'right' });

    y += 14;

    const { body, totals } = this.buildTableBody(stops, tour.legs || []);

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [[
        'Nr.',
        'KAUFVERTRAG / KUNDE\n(Adresse / Kontaktdaten)',
        'ZEITFENSTER',
        'ARTIKEL / LEISTUNG',
        'VOLUMEN\n(m³)',
        'GEWICHT\n(kg)',
        'KOLLI',
        'ME\n(=5 Min)',
        'MONTAGEZEIT\n(Min)',
        'ENTFERNUNG zum nächsten Kunden\n(km / ca. Min)',
        'BESONDERHEITEN / HINWEISE'
      ]],
      body,
      styles: {
        fontSize: 7,
        cellPadding: 1.5,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        valign: 'top'
      },
      headStyles: {
        fillColor: [90, 90, 90],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 6.5
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 48 },
        2: { cellWidth: 18, halign: 'center' },
        3: { cellWidth: 34 },
        4: { cellWidth: 14, halign: 'right' },
        5: { cellWidth: 14, halign: 'right' },
        6: { cellWidth: 10, halign: 'center' },
        7: { cellWidth: 11, halign: 'center' },
        8: { cellWidth: 14, halign: 'center' },
        9: { cellWidth: 24, halign: 'center' },
        10: { cellWidth: 38 }
      },
      didDrawPage: data => {
        const pageCount = doc.getNumberOfPages();
        doc.setFontSize(7);
        doc.setTextColor(100);
        doc.text(
          `Seite ${data.pageNumber} / ${pageCount}`,
          pageWidth - margin,
          doc.internal.pageSize.getHeight() - 4,
          { align: 'right' }
        );
        doc.setTextColor(0);
      }
    });

    const tableEndY = (doc as any).lastAutoTable?.finalY ?? y + 20;
    this.drawTourSummary(doc, margin, tableEndY + 3, totals, tour);

    const ts = (tour.generatedAt || new Date()).toISOString().slice(0, 10);
    const teamSuffix = tour.teamNumber ? `_team${tour.teamNumber}` : '';
    doc.save(`rollkarte${teamSuffix}_${ts}.pdf`);
  }

  private buildTableBody(
    stops: DispatchStop[],
    legs: TourRouteLeg[]
  ): { body: string[][]; totals: RollCardTotals } {
    const body: string[][] = [];
    const totals: RollCardTotals = { volumen: 0, gewicht: 0, kollo: 0, me: 0, montageMin: 0 };

    stops.forEach((stop, index) => {
      const me = stop.montagemenge ?? 0;
      const montageMin = Math.round(me * 5);
      const volumen = stop.volumenM3 ?? 0;
      const gewicht = stop.gewichtKg ?? 0;
      const kollo = this.resolveKollo(stop);

      totals.volumen += volumen;
      totals.gewicht += gewicht;
      totals.kollo += kollo;
      totals.me += me;
      totals.montageMin += montageMin;

      const leg = legs[index];
      const distanceCell = index < stops.length - 1 && leg
        ? `${leg.distance_text} / ${leg.duration_text}`
        : 'Tourende';

      body.push([
        this.formatNrCell(stop),
        this.formatCustomerCell(stop),
        this.formatZeitfenster(stop.termin),
        this.formatArtikelLeistung(stop),
        volumen > 0 ? this.formatDecimal(volumen, 3) : '',
        gewicht > 0 ? this.formatDecimal(gewicht, 1) : '',
        kollo > 0 ? String(kollo) : '',
        me > 0 ? this.formatDecimal(me, 0) : '',
        montageMin > 0 ? String(montageMin) : '',
        distanceCell,
        this.formatHinweise(stop)
      ]);
    });

    return { body, totals };
  }

  private drawTourSummary(
    doc: jsPDF,
    margin: number,
    startY: number,
    totals: RollCardTotals,
    tour: DispatchTourMeta
  ): void {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = startY;

    if (y > pageHeight - 28) {
      doc.addPage();
      y = margin;
    }

    doc.setDrawColor(0);
    doc.setLineWidth(0.2);
    doc.line(margin, y, pageWidth - margin, y);
    y += 4;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('TOURENÜBERSICHT', margin, y);
    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    const summaryParts = [
      `Gesamt Volumen: ${this.formatDecimal(totals.volumen, 3)} m³`,
      `Gesamt Gewicht: ${this.formatDecimal(totals.gewicht, 1)} kg`,
      `Gesamt Kolli: ${totals.kollo}`,
      `Gesamt ME: ${this.formatDecimal(totals.me, 0)}`,
      `Gesamt Montagezeit: ${totals.montageMin} Min (${this.formatDurationHours(totals.montageMin)})`
    ];
    if (tour.distanceText || tour.durationText) {
      summaryParts.push(`Strecke: ${tour.distanceText || '–'} / ${tour.durationText || '–'}`);
    }
    doc.text(summaryParts.join('   ·   '), margin, y);
    y += 6;

    doc.setFontSize(7);
    doc.text('Legende:', margin, y);
    doc.text('Li = Lieferung   ·   LiMo = Lieferung + Montage   ·   Mo = Montage   ·   R = Reklamation', margin + 16, y);
    y += 5;

    doc.setFontSize(6.5);
    doc.setTextColor(80);
    doc.text('1 ME (Montageeinheit) entspricht 5 Minuten Montagezeit.', margin, pageHeight - 8);
    doc.text(
      'Bitte nach jedem Auftrag vom Kunden unterschreiben lassen.',
      pageWidth - margin,
      pageHeight - 8,
      { align: 'right' }
    );
    doc.setTextColor(0);
  }

  private formatNrCell(stop: DispatchStop): string {
    const symbol = this.getServiceSymbol(stop);
    return `${stop.orderIndex}\n${symbol}`;
  }

  private getServiceSymbol(stop: DispatchStop): string {
    const art = (stop.auftragsart || '').trim().toUpperCase();
    if (art === 'R' || art.includes('REKLA')) {
      return 'R';
    }
    const type = getStopServiceTypeLabel(stop);
    return type || '–';
  }

  private formatCustomerCell(stop: DispatchStop): string {
    const lines: string[] = [];
    if (stop.kaufvertragsnummer) {
      lines.push(stop.kaufvertragsnummer);
    }
    if (stop.customerName) {
      lines.push(stop.customerName);
    } else if (stop.nachname) {
      lines.push(stop.nachname);
    }
    if (stop.address) {
      lines.push(stop.address);
    }
    const phone = (stop.mobil || stop.telefon || '').trim();
    if (phone) {
      lines.push(phone);
    }
    return lines.join('\n') || '–';
  }

  private formatZeitfenster(termin?: string): string {
    const raw = (termin || '').trim();
    if (!raw) {
      return '';
    }

    const timeRange = /(\d{1,2}:\d{2})\s*[-–]\s*(\d{1,2}:\d{2})/.exec(raw);
    if (timeRange) {
      return `${timeRange[1]} - ${timeRange[2]}`;
    }

    const singleTime = /(\d{1,2}:\d{2})/.exec(raw);
    if (singleTime && !/^\d{4}-\d{2}-\d{2}/.test(raw) && !/^\d{1,2}\.\d{1,2}\.\d{4}/.test(raw)) {
      return singleTime[1];
    }

    return '';
  }

  private formatArtikelLeistung(stop: DispatchStop): string {
    const articleLabel = this.getPrimaryArticleLabel(stop);
    const service = getStopServiceTypeFullLabel(stop);
    const lines = [articleLabel, service].filter(Boolean);
    return lines.join('\n') || '–';
  }

  private getPrimaryArticleLabel(stop: DispatchStop): string {
    const articles = stop.articles || [];
    if (articles.length === 1) {
      return (articles[0].bezeichnung || '').trim();
    }
    if (articles.length > 1) {
      return articles
        .map(a => (a.bezeichnung || '').trim())
        .filter(Boolean)
        .join(', ');
    }
    if (stop.positionCount || stop.artikelCount) {
      return `${stop.positionCount || stop.artikelCount} Position(en)`;
    }
    return '';
  }

  private formatHinweise(stop: DispatchStop): string {
    const parts = [
      stop.zusatztext,
      stop.tourBezeichnung,
      stop.kontingentLabel && stop.kontingentLabel !== 'Sonstiges' ? stop.kontingentLabel : '',
      stop.lieferfaehigSeit ? `Lieferfähig seit ${formatDispatchTermin(stop.lieferfaehigSeit)}` : ''
    ].filter(part => !!(part || '').trim());
    return parts.join('\n');
  }

  private resolveKollo(stop: DispatchStop): number {
    if ((stop.liefermenge ?? 0) > 0) {
      return Math.round(stop.liefermenge!);
    }
    if (stop.artikelCount) {
      return stop.artikelCount;
    }
    return stop.articles?.length ?? 0;
  }

  private resolveTourDate(stops: DispatchStop[], generatedAt?: Date): string {
    for (const stop of stops) {
      const formatted = formatDispatchTermin(stop.termin);
      if (formatted) {
        return formatted;
      }
    }
    return (generatedAt || new Date()).toLocaleDateString('de-DE');
  }

  private formatDecimal(value: number, decimals: number): string {
    return value.toLocaleString('de-DE', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }

  private formatDurationHours(minutes: number): string {
    if (!minutes) {
      return '0 Min';
    }
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (!hours) {
      return `${rest} Min`;
    }
    return rest ? `${hours} Std. ${rest} Min` : `${hours} Std.`;
  }
}
